import { sentenceCase } from "@redotech/string/transform";
import { Condition } from "./condition";

/**
 * Condition names as merchants see them, surfaced in flow-edit approval
 * summaries; keyed by Condition symbols so an unnamed condition cannot compile.
 */
export const CONDITION_NAMES = {
  [Condition.ADJUSTMENT_AMOUNT]: "Total adjustment amount",
  [Condition.AFTER_HOURS]: "Customer contacted after hours",
  [Condition.ASSIGNMENT_TYPE]: "Assignment",
  [Condition.CHANNEL]: "Return channel",
  [Condition.COLLECTION]: "Collections",
  [Condition.COMPARE_AT_PRICE]: "Compare-at price",
  [Condition.COMPENSATION_METHOD]: "Compensation method",
  [Condition.COVERAGE]: "Returns covered by Redo",
  [Condition.CUSTOMER_ADDRESS]: "Customer address",
  [Condition.CUSTOMER_COUNTRY]: "Customer country",
  [Condition.CUSTOMER_EMAIL]: "Customer email",
  [Condition.CUSTOMER_EMAIL_SUBSCRIBER]: "Customer email subscriber",
  [Condition.CUSTOMER_SMS_SUBSCRIBER]: "Customer sms subscriber",
  [Condition.CUSTOMER_TAG]: "Customer tags",
  [Condition.DAMAGED]: "Damaged",
  [Condition.DELIVERY_WITHIN]: "Time since delivery",
  [Condition.DISCOUNT]: "Discounts",
  [Condition.DROPOFF_NEARBY]: "Drop-off nearby",
  [Condition.EXCHANGE_COUNT]:
    "Number of previous exchanges / is order an exchange",
  [Condition.EXTENDED_WARRANTY]: "Extended warranty is live on item",
  [Condition.EXTENDED_WARRANTY_PURCHASED]: "Extended warranty purchased",
  [Condition.FINAL_SALE_RETURN]: "Final sale return item",
  [Condition.FULFILLMENT_TRACKING_UPDATE_WITHIN]:
    "Time since last tracking update",
  [Condition.FULFILLMENT_WITHIN]: "Time since fulfillment",
  [Condition.IS_DELIVERED]: "Package delivered",
  [Condition.IS_GIFT_RETURN]: "Gift return",
  [Condition.IS_GREEN_RETURN]: "Green return",
  [Condition.IS_REFUND_RETURN]: "Requested refund on return",
  [Condition.IS_REPAIR]: "Is repair",
  [Condition.ITEM_DISCOUNT]: "Item discount",
  [Condition.ITEMS_BEING_RETURNED_COUNT]: "Number of items being returned",
  [Condition.LOCATION_NAME]: "Origin location name",
  [Condition.LOYALTY_EARNED]: "Loyalty",
  [Condition.MERCHANT_CREATED]: "Merchant created",
  [Condition.MESSAGE_BODY_MATCHES]: "Message body",
  [Condition.MULTIPLE_CHOICE]: "Multiple choice",
  [Condition.NON_RETURNED_VALUE]: "Non-returned value",
  [Condition.NUM_CUSTOMER_CLAIMS]: "Number of previous customer claims",
  [Condition.NUM_CUSTOMER_RETURNS]: "Number of previous customer returns",
  [Condition.NUM_ITEMS_IN_RETURN]: "Number of items in return",
  [Condition.NUM_ORDERS]: "Number of orders",
  [Condition.ORDER_COUNT_GREATER_THAN]: "Order count greater than",
  [Condition.ORDER_DATE]: "Order created date",
  [Condition.ORDER_NOTE]: "Order note",
  [Condition.ORDER_NUMBER]: "Order number",
  [Condition.ORDER_TAG]: "Order tags",
  [Condition.ORDER_TIME_COLLECTION]: "Original collections at order time",
  [Condition.ORDER_WITHIN]: "Time since order",
  [Condition.ORDERLESS]: "Third Party Order",
  [Condition.PACKAGE_PROTECTION]: "Covered by package protection",
  [Condition.PAYMENT_METHOD]: "Original Payment Method",
  [Condition.PLATFORM]: "Platform",
  [Condition.PRICE]: "Pre-Discount Price",
  [Condition.PRODUCT_AVAILABLE]: "Product available",
  [Condition.PRODUCT_METAFIELD]: "Product metafield",
  [Condition.PRODUCT_PROPERTY]: "Product properties",
  [Condition.PRODUCT_TAG]: "Product tags",
  [Condition.PRODUCT_TYPE]: "Product type",
  [Condition.PRODUCT_VARIANT_METAFIELD]: "Product variant metafield",
  [Condition.PURCHASE_CHANNEL]: "Purchase Channel",
  [Condition.RECEIVED_AT_EMAIL]: "Email Received At",
  [Condition.REPLACEMENT_UNAVAILABLE]: "Replacement unavailable",
  [Condition.RETURN_DATE]: "Request created date",
  [Condition.RETURN_METHOD]: "Return method",
  [Condition.RETURN_RATE]: "Customer return rate",
  [Condition.RETURN_REASON]: "Return reason",
  [Condition.RETURN_TOGETHER_ITEM_RETURNED_SEPARATELY]:
    "Bundle item being returned individually",
  [Condition.RETURN_TYPE]: "Return type",
  [Condition.RETURN_VALUE]: "Individual item price (deprecated)",
  [Condition.SALES_CHANNEL]: "Sales channel",
  [Condition.SENT_FROM_EMAIL]: "Email sent from",
  [Condition.SKU]: "SKU",
  [Condition.SUBJECT_LINE]: "Subject line",
  [Condition.TICKET_CHANNEL]: "Ticket channel",
  [Condition.TOPIC]: "Message topic",
  [Condition.TOTAL_PRE_DISCOUNT_PRICE]: "Total pre-discount price",
  [Condition.TOTAL_RETURN_VALUE]: "Total Return Value",
  [Condition.TOTAL_SPENT_GREATER_THAN]: "Total spent greater than",
  [Condition.TRACKING_TYPE]: "Tracking type",
  [Condition.VENDOR]: "Vendors",
  [Condition.WEEKLY_SCHEDULE]: "Weekly schedule",
  [Condition.WITHDRAWAL_EXCLUSION]: "Withdrawal exclusion",
  [Condition.WRONG_PRODUCT]: "Wrong product",
} as const satisfies Record<Condition["type"], string>;

const NAMES_BY_WIRE_TYPE = new Map<string, string>();
for (const value of Object.values(Condition)) {
  if (typeof value === "symbol" && value.description) {
    NAMES_BY_WIRE_TYPE.set(value.description, CONDITION_NAMES[value]);
  }
}

/** Display name for a wire-format condition type string, e.g. "order_within". */
export function conditionName(type: unknown): string {
  if (typeof type !== "string" || !type) {
    return "Condition";
  }
  return NAMES_BY_WIRE_TYPE.get(type) ?? sentenceCase(type);
}
